/**
 * RightPanelSlot — slot/portal para el panel derecho del ProjectShell.
 *
 * El ProjectShell renderiza un `<aside>` siempre montado a la derecha
 * del área de trabajo y lo expone como "host" vía contexto. Las rutas
 * hijas (editor, viewer) llaman `useRightPanel(node)` y el contenido
 * se inyecta en ese host con `createPortal`.
 *
 * Por qué portal y no `setState({ node })` en el shell:
 *   - El nodo pasado al hook es JSX y cambia de referencia cada render
 *     del padre. Si guardáramos el nodo en un state del shell vía
 *     `setPanel(node)` desde un `useEffect`, ese effect dispararía cada
 *     render → setState → re-render del shell → re-render del padre →
 *     nuevo nodo → loop infinito ("Maximum update depth exceeded").
 *   - Con portal, React lleva los hijos al DOM del host directamente.
 *     No hay state intermediario, no hay loop, y el unmount del padre
 *     limpia el portal automáticamente.
 *
 * El `<aside>` host usa la utilidad `empty:hidden` para sacarse del
 * layout cuando ningún slot está activo, así el área del editor llena
 * todo el ancho disponible cuando el panel está cerrado.
 *
 * Uso:
 *
 *   function EditorPanelSlot({ open, ... }: Props) {
 *     return useRightPanel(open ? <RightSidePanel ... /> : null)
 *   }
 *
 * Si dos rutas montan slots simultáneamente, ambos contenidos se
 * renderizan en el host (uno tras otro). En la práctica solo hay una
 * ruta hija activa a la vez.
 */

import { createContext, useContext, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface RightPanelContextValue {
  /** DOM node del `<aside>` que sirve como host del portal. `null`
   *  hasta que el shell ha montado y la ref-callback se disparó. */
  host: HTMLElement | null
}

const RightPanelContext = createContext<RightPanelContextValue | null>(null)

export const RightPanelProvider = RightPanelContext.Provider

/**
 * Inyecta `node` en el host del panel derecho del ProjectShell.
 * Devuelve el portal node ya creado — el componente que llama debe
 * retornarlo para que React reconozca el portal en el árbol y aplique
 * mount/unmount correctamente.
 *
 * Si no hay host disponible (fuera de un ProjectShell o aún sin mount)
 * o `node` es `null`, devuelve `null` y no se renderiza nada.
 */
export function useRightPanel(node: ReactNode | null): ReactNode | null {
  const ctx = useContext(RightPanelContext)
  if (!ctx?.host) return null
  if (node === null || node === undefined) return null
  return createPortal(node, ctx.host)
}
